/**
 * Recurrence helpers for Senior Center activities.
 * Centers are closed Sat/Sun — weekly / monthly day picks are Mon–Fri only.
 */

export const ACTIVITY_WEEKDAY_OPTIONS = [
  { label: 'M', value: 'Mon', full: 'Monday' },
  { label: 'Tu', value: 'Tue', full: 'Tuesday' },
  { label: 'W', value: 'Wed', full: 'Wednesday' },
  { label: 'Th', value: 'Thu', full: 'Thursday' },
  { label: 'Fr', value: 'Fri', full: 'Friday' },
] as const;

export const ACTIVITY_MONTHLY_ORDINALS = ['1st', '2nd', '3rd', '4th'] as const;

export type ActivityMonthlyOrdinal = (typeof ACTIVITY_MONTHLY_ORDINALS)[number];

/** Stored / imported values, e.g. "1st Monday", "2nd Tuesday". */
export const ACTIVITY_MONTHLY_OCCURRENCE_OPTIONS: string[] = ACTIVITY_MONTHLY_ORDINALS.flatMap(
  (ordinal) => ACTIVITY_WEEKDAY_OPTIONS.map((day) => `${ordinal} ${day.full}`),
);

const WEEKDAY_FULL_BY_SHORT: Record<string, string> = Object.fromEntries(
  ACTIVITY_WEEKDAY_OPTIONS.map((d) => [d.value.toLowerCase(), d.full]),
);

const WEEKDAY_SHORT_BY_FULL: Record<string, string> = Object.fromEntries(
  ACTIVITY_WEEKDAY_OPTIONS.map((d) => [d.full.toLowerCase(), d.value]),
);

export function buildMonthlyOccurrence(
  ordinal: string | null | undefined,
  weekday: string | null | undefined,
): string | null {
  const ord = String(ordinal ?? '').trim();
  const dayRaw = String(weekday ?? '').trim();
  if (!ord || !dayRaw) {
    return null;
  }
  const normalizedFull =
    WEEKDAY_FULL_BY_SHORT[dayRaw.toLowerCase()] ||
    ACTIVITY_WEEKDAY_OPTIONS.find((d) => d.full.toLowerCase() === dayRaw.toLowerCase())?.full ||
    '';
  if (!ACTIVITY_MONTHLY_ORDINALS.includes(ord as ActivityMonthlyOrdinal) || !normalizedFull) {
    return null;
  }
  return `${ord} ${normalizedFull}`;
}

export function parseMonthlyOccurrence(
  value: string | null | undefined,
): { ordinal: ActivityMonthlyOrdinal; weekday: string } | null {
  const raw = String(value ?? '').trim();
  if (!raw) {
    return null;
  }
  const match = raw.match(/^(1st|2nd|3rd|4th)\s+(Mon|Tue|Wed|Thu|Fri|Monday|Tuesday|Wednesday|Thursday|Friday)$/i);
  if (!match) {
    return null;
  }
  const ordinal = (match[1].charAt(0) + match[1].slice(1).toLowerCase()) as ActivityMonthlyOrdinal;
  const dayToken = match[2];
  const weekday =
    WEEKDAY_SHORT_BY_FULL[dayToken.toLowerCase()] ||
    ACTIVITY_WEEKDAY_OPTIONS.find((d) => d.value.toLowerCase() === dayToken.toLowerCase())?.value ||
    '';
  if (!ACTIVITY_MONTHLY_ORDINALS.includes(ordinal) || !weekday) {
    return null;
  }
  return { ordinal, weekday };
}

export function isMonthlyOccurrenceValue(value: string | null | undefined): boolean {
  return parseMonthlyOccurrence(value) != null;
}

export function normalizeWeeklyDay(value: string | null | undefined): string | undefined {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) {
    return undefined;
  }
  const map: Record<string, string> = {
    m: 'Mon',
    mon: 'Mon',
    monday: 'Mon',
    tu: 'Tue',
    tue: 'Tue',
    tues: 'Tue',
    tuesday: 'Tue',
    w: 'Wed',
    wed: 'Wed',
    wednesday: 'Wed',
    th: 'Thu',
    thu: 'Thu',
    thur: 'Thu',
    thurs: 'Thu',
    thursday: 'Thu',
    f: 'Fri',
    fr: 'Fri',
    fri: 'Fri',
    friday: 'Fri',
  };
  return map[trimmed.toLowerCase()];
}

export function normalizeMonthlyOccurrence(value: string | null | undefined): string | undefined {
  const parsed = parseMonthlyOccurrence(value);
  if (!parsed) {
    return undefined;
  }
  return buildMonthlyOccurrence(parsed.ordinal, parsed.weekday) ?? undefined;
}
